import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Countdown that starts now and runs for `initialDays` days.
 * Keeps counting down even if the user keeps the page open.
 */
export function FourteenDayCounterStartNow({
  startDate,
  initialDays = 14,
  // initialDays = 1, // For testing
}: {
  startDate: string | Date;
  initialDays?: number;
}) {
  const startTs = useMemo(() => new Date(startDate).getTime(), [startDate]);

  const targetTs = useMemo(
    () => startTs + initialDays * 24 * 60 * 60 * 1000,
    [startTs, initialDays]
  );

  return <DaysToGoDisplay targetTs={targetTs} />;
}

/** Shared component that displays "X days to go" given a target timestamp (ms) */
function DaysToGoDisplay({ targetTs }: { targetTs: number }) {
  const { t } = useTranslation();

  const getDaysLeft = () => {
    const now = new Date();
    const todayUTC = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    );

    const targetDate = new Date(targetTs);
    const targetUTC = Date.UTC(
      targetDate.getUTCFullYear(),
      targetDate.getUTCMonth(),
      targetDate.getUTCDate()
    );

    const diffDays = Math.ceil((targetUTC - todayUTC) / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const [daysLeft, setDaysLeft] = useState<number>(() => getDaysLeft());

  useEffect(() => {
    // Update only once per day at midnight — every 60 seconds for safety
    const id = setInterval(() => {
      setDaysLeft(getDaysLeft());
    }, 60_000);

    setDaysLeft(getDaysLeft());

    return () => clearInterval(id);
  }, [targetTs]);

  return (
    <div aria-live="polite">
      {daysLeft > 0 && (
        <span className="text-sm text-red-500">
          ({t("days_to_go", {
            days_left: daysLeft,
            days: daysLeft === 1 ? `${t("day")}` : `${t("days")}`,
          })})
        </span>
      )}
    </div>
  );
}
